import { useEffect, useRef } from "react";
import cn from "classnames";
import { cardToDeleteStore } from "features/CardToDelete";
import { filteredCardsStore } from "features/Filter";
import { useFilterContext } from "features/Filter/ui/context/Filter.context";
import { LoadMoreButton } from "features/LoadMoreButton";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { createSeaUpdateCardHook, useSeaCardActions } from "features/SeaCardActions";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore, useTagsContext } from "features/Tags";
import { authStore } from "entities/Auth";
import { Card, LoadCard } from "entities/Card";
import { PanelView } from "entities/PanelView";
import { searchFilterStore } from "entities/SearchFilter";
import {
  isSeaSavedShipmentModel,
  SEA_FILTERED_CARDS_QUERY_CACHE_KEY,
  SeaSavedCard,
  useSeaInfiniteScrollFetch,
} from "entities/SeaSavedCard";
import { CARDS_REQUEST_LIMIT } from "shared/constants/common.constants";
import { savedShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import s from "../FilteredCardsView.module.scss";

type SeaFilteredCardsViewProps = {
  className?: string;
};

export const SeaFilteredCardsView: RFC<SeaFilteredCardsViewProps> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const containerRef = useRef(null);
  const { filter, filterChanged, isSeaFilter } = useFilterContext();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const filteredCards = useAppSelector(filteredCardsStore.selectors.filteredCards);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const { setFilteredCardsViewHistory, currentHistoryState } = usePanelViews();
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });
  const { refreshTags } = useTagsContext();
  const { deleteSeaCardHandler, isSeaCardDeleting } = useSeaCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const seaFilteredCards = filteredCards.filter((card) => isSeaSavedShipmentModel(card));

  const {
    requestMoreCards,
    setRequestFilterSync: setRequestFilter,
    shouldRenderSeaMoreButton,
    shouldRenderSeaInfiniteQueryLoader,
    cancelGetCardsRequest,
    resetSeaCardsQuery: resetCardsQuery,
  } = useSeaInfiniteScrollFetch<HTMLDivElement>({
    containerRef,
    onBeforeRequest: () => {
      if (isSeaFilter?.(filter)) setRequestFilter({ where: filter.where, order: filter.order });
    },
    onSuccess: ({ page }) => {
      dispatch(filteredCardsStore.actions.appendFilteredCardsAction({ data: page }));
    },
    onSettled: ({ totalCount }) => {
      setFilteredCardsViewHistory({ ...filter, totalCount });
    },
    shouldRequestOnMount: () => {
      if (!isAuthenticated) return false;

      const baseCondition = seaFilteredCards.length < CARDS_REQUEST_LIMIT;

      if (currentHistoryState?.totalCount) {
        return baseCondition && currentHistoryState.totalCount > CARDS_REQUEST_LIMIT;
      }

      return baseCondition;
    },
    queryKey: [SEA_FILTERED_CARDS_QUERY_CACHE_KEY],
    initialFilter: currentHistoryState?.filter,
  });

  useEffect(() => {
    if (filterChanged()) {
      resetCardsQuery();
      dispatch(filteredCardsStore.actions.clearFilteredCardsAction());
    }
  }, [filter]);

  useEffect(
    () => () => {
      resetCardsQuery();
      cancelGetCardsRequest();
    },
    []
  );

  if (!seaFilteredCards.length && !shouldRenderSeaInfiniteQueryLoader) {
    return (
      <PanelView ref={containerRef} className={cn(s.containerFilterEmpty, className)} shouldRenderMoreButton={false}>
        <div className={s.emptyContentWrapper}>
          <div className={s.wrapperFilterEmpty}>
            <h3 className={s.emptyTitle}>
              {t("No results for the selected parameter. Please change the filter options")}.
            </h3>
          </div>
        </div>
      </PanelView>
    );
  }

  return (
    <PanelView
      ref={containerRef}
      className={className}
      shouldRenderMoreButton={shouldRenderSeaMoreButton}
      loadMoreButton={<LoadMoreButton onClick={requestMoreCards} />}
    >
      {seaFilteredCards.map((card) => (
        <SeaSavedCard
          key={card.reference}
          data={card}
          openCard={openCard}
          isSavedCardDeleting={isSeaCardDeleting}
          useSavedCardUpdateActions={createSeaUpdateCardHook({
            model: card,
            isOpenedCardExist: !!openedCard,
            openedCardUpdateAction: (response) => ({
              type: openedCardStore.actions.updateOpenCardAction.type,
              payload: response,
            }),
            updateFilteredCards: (response) => ({
              type: filteredCardsStore.actions.updateFilterCardAction.type,
              payload: response,
            }),
            updateCardByTagAction: (response) => ({
              type: tagsStore.actions.updateCardByTagAction.type,
              payload: response,
            }),
          })}
          deleteCardHandler={deleteSeaCardHandler}
          shipmentType={shipmentType}
          renderCard={(props) => <Card {...props} />}
        />
      ))}

      {shouldRenderSeaInfiniteQueryLoader && <LoadCard text={t(savedShipmentLoadingText)} />}
    </PanelView>
  );
};
